import { ItemDiscountInterface } from "@/interface/business/discount.interface";
import { ItemDiscountType } from "@/types/business/discount.type";
import { DISCOUNT_TYPES } from "@/enums/business.enum";
import PriceMoney from "@/modules/location/price";
import { DISCOUNT_APPLIED_ON } from "@/enums/orders.enum";
import Storage from "@/services/storage";
import { StorageConstants } from "@/enums/storage.enum";

export interface ItemDiscountDayTime {
  start: string;
  end: string;
}

export interface ItemDiscountDays {
  day: string;
  time: ItemDiscountDayTime;
}

export default class ItemDiscount implements ItemDiscountInterface {
  id: string = "";
  name: string = "";
  type: string = "";
  amount: number = 0;
  rate: number = 0;
  color: string = "";
  pin_required: boolean = false;
  after_tax: boolean = false;
  item_applicable: boolean = false;
  automatic: boolean = false;
  days: Array<ItemDiscountDays> = [];
  isValidNow: boolean = false;
  disabled: boolean = false;
  applied_on: string = DISCOUNT_APPLIED_ON.ITEM;
  is_custom: boolean = false;
  error?: string = "";
  currency?: string = "";

  constructor(data: ItemDiscountType = null) {
    if (data) {
      Object.assign(this, data);
      this.setIsValidNow();
    }

    this.prepareIsCustom();
    this.currency = Storage.get(StorageConstants.BUSINESS)?.currency;
  }

  /**
   * Setup the discount is Custom
   * @private
   * @return {void}
   */
  private prepareIsCustom(): void {
    // TODO comment changes till the BE fix the is_custom flag on items + V2 complete the changes on setting
    // this.is_custom =
    //   (this.type === DISCOUNT_TYPES.FIXED && !this.amount) || (this.type === DISCOUNT_TYPES.PERCENTAGE && !this.rate);
  }

  /**
   * handle on set the discount amount
   * @param amount
   */
  setAmount(amount: number): void {
    this.amount = amount * 1000;
  }

  /**
   * handle on set the discount rate
   * @param rate
   */
  setRate(rate: number): void {
    this.rate = rate / 100;
  }

  /***
   * Set is Discount scheduled available now
   * @return {void | *}
   */
  private setIsValidNow(): void {
    if (window._.isEmpty(this.days)) {
      return;
    }

    // compare the day with today
    const getIsValidDay = (day: string): boolean => {
      return day.toLowerCase() === window.moment(new Date()).format("dddd").toLowerCase();
    };

    // get today discount
    const today = this.days.find((currentDay) => getIsValidDay(currentDay.day.toLowerCase()));

    // if not today then it is not discount available today
    if (!today) {
      this.isValidNow = false;
      this.disabled = true;
      return;
    }

    // else if today have discount then check the now time is in range times
    const startTime = today?.time?.start;
    const endTime = today?.time?.end;
    const todayDate = new window.moment().format("YYYY-MM-DD");

    const start = window.moment(`${todayDate} ${startTime}`);
    const end = window.moment(`${todayDate} ${endTime}`);

    this.isValidNow = end?.isAfter() && start?.isBefore();
    this.disabled = !this.isValidNow;
  }

  /***
   * Prepare the discount value
   * @return {string}
   */
  public getInputDiscountValueLabel(): string {
    if (this.is_custom) {
      return this.getCustomDiscountLabel();
    }

    return this.getValue();
  }

  /***
   * Prepare the discount input label value
   * @return {string}
   */
  public getCustomDiscountLabel(): string {
    if (this.type === DISCOUNT_TYPES.FIXED) {
      return `Custom ${this.currency}`;
    }

    return `Custom %`;
  }

  /**
   * Get Value % or Price
   * @return {String} price || rate %
   */
  getValue(): string {
    if (this.type === DISCOUNT_TYPES.FIXED) {
      return new PriceMoney({ amount: this.amount }).getPricePackage();
    }

    let rate = (this.rate * 100).toFixed(1);
    return `${rate}%`;
  }

  /**
   * Get Value input
   * @return {any}
   */
  public getInputValue(): any {
    if (this.type === DISCOUNT_TYPES.FIXED) {
      return this.amount / 1000;
    }

    return this.rate * 100;
  }

  /**
   * Set Discount disable
   * @return {void}
   */
  disableDiscount(): void {
    this.disabled = true;
  }

  /**
   * Update the applied_on
   * @param type DISCOUNT_APPLIED_ON
   */
  public setAppliedOn(type: string): void {
    this.applied_on = type;
  }

  /**
   * Validate the discount value
   * @return {boolean}
   */
  public validateValue(): boolean {
    if (!this.is_custom) {
      return true;
    }

    return !!this.amount || !!this.rate;
  }
}
